/**
 * ============================================================================
 *  FEATURE FLAGS — the single place to turn whole features on/off.
 * ============================================================================
 *
 *  Flip one boolean here to enable or disable an entire feature. No other code
 *  needs to change: every feature checks its flag through this object, and the
 *  service boundaries (ads, payments, skins, auth, network) have working stub
 *  implementations behind them, so turning a flag ON later means implementing
 *  behind an existing interface, never re-architecting.
 *
 *  v1 ships with monetization DORMANT (ads/payments/skins/adFree = false).
 */
export interface FeatureFlags {
  /** Rewarded-video ads in the respawn flow. OFF in v1 (dormant scaffolding). */
  ads: boolean;
  /** Pay-to-remove-ads option. OFF in v1 (dormant scaffolding). */
  adFree: boolean;
  /** Payments / billing boundary. OFF in v1 (stubbed, no real provider). */
  payments: boolean;
  /** Cosmetic skins catalog + selection UI. OFF in v1 (dormant scaffolding). */
  skins: boolean;

  /** Local persisted high-score leaderboard. ON. */
  leaderboard: boolean;
  /** Auto-select on-screen joystick + buttons on touch devices. ON. */
  mobileControls: boolean;
  /** Give bots lobby-style names (from src/config/botNames.ts). ON. When OFF,
   *  bots spawn unnamed. */
  botNames: boolean;
  /** Register the service worker / installable PWA. ON. */
  pwa: boolean;

  /** Camera zooms out as the player grows. ON. */
  cameraZoom: boolean;
  /** Player-driven zoom (scroll wheel / pinch), clamped to config bounds. ON. */
  manualZoom: boolean;
  /** Corner minimap showing the arena, entities, and the current viewport. ON. */
  minimap: boolean;
  /** Render interpolation between fixed sim steps (smoother motion). ON. */
  renderInterpolation: boolean;
  /** Draw the background grid. ON (purely cosmetic). */
  backgroundGrid: boolean;
  /** Developer overlay (FPS, entity count). OFF. */
  debugOverlay: boolean;
}

export const FEATURES: FeatureFlags = {
  // --- Monetization: dormant in v1 ---
  ads: false,
  adFree: false,
  payments: false,
  skins: false,

  // --- Core experience: on ---
  leaderboard: true,
  mobileControls: true,
  botNames: true,
  pwa: true,

  // --- Presentation toggles ---
  cameraZoom: true,
  manualZoom: true,
  minimap: true,
  renderInterpolation: true,
  backgroundGrid: true,
  debugOverlay: false,
};
